import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import {
    ExpertAreasFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    Input,
    NotificationPriority,
    NotificationSeverity,
    SeverityFilter,
    PriorityFilter,
    useDebounced,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { ProfessionalField, ProfessionalFieldFilterFields } from "modules/common/constants/professionalFieldTypes";
import { DateRangePicker } from "modules/common/components/DateRangePicker/DateRangePicker";
import { SentNotificationsFilter, mapToFilterName } from "modules/common/utils/filters";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { UpdateFilterPreferencesRequest } from "services";

const INPUT_DEBOUNCE_TIME = 500; //miliseconds

interface Props {
    priorityFilter: NotificationPriority[];
    setPriorityFilter: React.Dispatch<React.SetStateAction<NotificationPriority[]>>;
    severityFilter: NotificationSeverity[];
    setSeverityFilter: (value: React.SetStateAction<NotificationSeverity[]>) => void;
    professionalFieldFilter: string[];
    setProfessionalFieldFilter: React.Dispatch<React.SetStateAction<string[]>>;
    searchFilter: string;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
    dateFilter: Date | null;
    setDateFilter: React.Dispatch<React.SetStateAction<Date | null>>;
    startDate: Date | null;
    setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
    endDate: Date | null;
    setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    onReturn: () => void;
}

export const SentNotificationsFilters: React.FC<Props> = ({
    priorityFilter,
    setPriorityFilter,
    severityFilter,
    setSeverityFilter,
    professionalFieldFilter,
    setProfessionalFieldFilter,
    searchFilter,
    setSearchFilter,
    dateFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setDateFilter,
    setCurrentPage,
    onReturn,
}) => {
    const professionalFieldOptions = useMemo(() => {
        return Object.entries(ProfessionalFieldFilterFields)
            .filter(([, { value }]) => value !== ProfessionalField.None)
            .map(([, { label }]) => label);
    }, []);

    const [search, setSearch] = useState(searchFilter);
    const debouncedSearch = useDebounced(search, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        setSearchFilter(debouncedSearch);
        setCurrentPage(1);
    }, [debouncedSearch]);

    // dropdown states
    const [showPriorityFilter, setShowPriorityFilter] = useState(false);
    const [showSeverityFilter, setShowSeverityFilter] = useState(false);
    const [showProfessionalFieldFilter, setShowProfessionalFieldFilter] = useState(false);

    // refs
    const priorityFilterRef = useRef<HTMLDivElement>(null);
    const severityFilterRef = useRef<HTMLDivElement>(null);
    const professionalFieldFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    useOutsideClick(priorityFilterRef, () => setShowPriorityFilter(false));
    useOutsideClick(severityFilterRef, () => setShowSeverityFilter(false));
    useOutsideClick(professionalFieldFilterRef, () => setShowProfessionalFieldFilter(false));

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        PRIORITY_FILTER_NAME,
        SEVERITY_FILTER_NAME,
        PROFESSIONAL_FIELD_FILTER_NAME,
    ]);

    // query
    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    useEffect(() => {
        if (filterPreferencesQuery.some(s => s.isLoading)) return;

        const priorityFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === PRIORITY_FILTER_NAME
        )?.data?.filterIntValues;

        const severityFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === SEVERITY_FILTER_NAME
        )?.data?.filterIntValues;

        const professionalFieldFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === PROFESSIONAL_FIELD_FILTER_NAME
        )?.data?.filterStringValues;

        if (priorityFilterData) setPriorityFilter(priorityFilterData);

        if (severityFilterData) setSeverityFilter(severityFilterData);

        if (professionalFieldFilterData) setProfessionalFieldFilter(professionalFieldFilterData);
    }, [filterPreferencesQuery]);

    // handle filter change based on filter type
    const handleUpdateFilterPreferences = (
        filterName: string,
        filterIntValue?: number,
        filterStringValue?: string,
        filterBoolValue?: boolean,
        clearFilter?: boolean
    ) => {
        const payload: UpdateFilterPreferencesRequest = {
            filterName,
            ...(filterIntValue !== undefined && { filterIntValue }),
            ...(filterStringValue !== undefined && { filterStringValue }),
            ...(filterBoolValue !== undefined && { filterBoolValue }),
            ...(clearFilter !== undefined && { clearFilter }),
        };
        updateFilterPreferencesMutation.mutate(payload);
        setCurrentPage(1);
    };

    // handle filter change
    const handlePriorityFilterChange = (priority: NotificationPriority) =>
        handleUpdateFilterPreferences(PRIORITY_FILTER_NAME, priority);

    const handleSeverityFilterChange = (severity: NotificationSeverity) =>
        handleUpdateFilterPreferences(SEVERITY_FILTER_NAME, severity);

    const handleProfessionalFieldFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleUpdateFilterPreferences(
            PROFESSIONAL_FIELD_FILTER_NAME,
            undefined,
            event.target.value
        );

    const handleDateFilterChange = (day: Date | null) => {
        setDateFilter(day);
        
        if (!startDate && !endDate) {
            setStartDate(day)
        } else if (startDate && !endDate) {
            setEndDate(day);
        } else if (startDate && endDate) {
            setStartDate(day);
            setEndDate(null);
        }
        
        setCurrentPage(1);
    };

    const handlePriorityFilterNameClear = () =>
        handleUpdateFilterPreferences(PRIORITY_FILTER_NAME, undefined, undefined, undefined, true);
    const handleSeverityFilterClear = () =>
        handleUpdateFilterPreferences(SEVERITY_FILTER_NAME, undefined, undefined, undefined, true);
    const handleProfessionalFieldFilterClear = () =>
        handleUpdateFilterPreferences(
            PROFESSIONAL_FIELD_FILTER_NAME,
            undefined,
            undefined,
            undefined,
            true
        );
    const handleSearchFilterClear = () => setSearchFilter("");
    const handleDateFilterClear = () => setDateFilter(null);
    const handleClearFilter = () => {
        handlePriorityFilterNameClear();
        handleSeverityFilterClear();
        handleProfessionalFieldFilterClear();
        handleSearchFilterClear();
        handleDateFilterClear();
    };

    // handle click
    const handlePriorityFilterClick = () => setShowPriorityFilter(prev => !prev);
    const handleSeverityFilterClick = () => setShowSeverityFilter(prev => !prev);
    const handleProfessionalFieldFilterClick = () => setShowProfessionalFieldFilter(prev => !prev);

    return (
        <div className="f g-24 even">
            <HeaderFilter
                title="POSLANE PORUKE"
                hasBackButton
                name="sent-notifications"
                onBackButtonClicked={onReturn}
            >
                <FilterGroup>
                    <div ref={priorityFilterRef}>
                        <PriorityFilter
                            onChange={handlePriorityFilterChange}
                            onClear={handlePriorityFilterNameClear}
                            onClick={handlePriorityFilterClick}
                            value={priorityFilter}
                            isActive={Boolean(priorityFilter.length)}
                            showDropdown={showPriorityFilter}
                        />
                    </div>

                    <div ref={severityFilterRef}>
                        <SeverityFilter
                            onChange={handleSeverityFilterChange}
                            onClear={handleSeverityFilterClear}
                            onClick={handleSeverityFilterClick}
                            value={severityFilter}
                            isActive={Boolean(severityFilter.length)}
                            showDropdown={showSeverityFilter}
                        />
                    </div>

                    <div ref={professionalFieldFilterRef}>
                        <ExpertAreasFilter
                            onChange={handleProfessionalFieldFilterChange}
                            onClear={handleProfessionalFieldFilterClear}
                            onClick={handleProfessionalFieldFilterClick}
                            value={professionalFieldFilter}
                            isActive={Boolean(professionalFieldFilter.length)}
                            showDropdown={showProfessionalFieldFilter}
                            options={professionalFieldOptions}
                            isLoading={false}
                        />
                    </div>

                    <FilterClear
                        show={
                            priorityFilter.length !== 0 ||
                            severityFilter.length !== 0 ||
                            professionalFieldFilter.length !== 0
                        }
                        onClearFilter={handleClearFilter}
                    />
                </FilterGroup>
            </HeaderFilter>

            <Input
                name="searchFilter"
                id={`${SentNotificationsFilters.name}-searchFilter`}
                type="text"
                size="small"
                placeholder="Pretraži poruke."
                onChange={e => setSearch(e.target.value)}
                value={search}
                leftIcon={<SearchIcon className="search-icon" />}
            />

            <DateRangePicker
                onClick={(day) => handleDateFilterChange(day)}
                value={{ from: startDate, to: endDate }}
            />
            
        </div>
    );
};

// map filter names
const PRIORITY_FILTER_NAME = mapToFilterName<SentNotificationsFilter>(
    SentNotificationsFilters.name,
    "priorityFilter"
);
const SEVERITY_FILTER_NAME = mapToFilterName<SentNotificationsFilter>(
    SentNotificationsFilters.name,
    "severityFilter"
);
const PROFESSIONAL_FIELD_FILTER_NAME = mapToFilterName<SentNotificationsFilter>(
    SentNotificationsFilters.name,
    "professionalFieldFilter"
);
